import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Application, Graphics } from 'pixi.js';
import {
  StudioSceneBackgroundDefinition,
  ImageAnimation,
} from '../../../element-editor/store/studio/studio.types';
import {
  CanvasService,
  _screenX,
  _screenY,
} from '../../../services/canvas/canvas.service';
import { CanvasBackground } from '../../../element-editor/shape/canvas-background';
import { BackgroundService } from '../../../element-editor/store/studio/background/background.service';

@Component({
  selector: 'nw-background-crop-preview',
  templateUrl: './background-crop-preview.component.html',
  styleUrls: ['./background-crop-preview.component.scss'],
})
export class BackgroundCropPreviewComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() backgroundDef: StudioSceneBackgroundDefinition | null = null;
  @Input() animation: ImageAnimation = {};

  @Output() cropChange = new EventEmitter<ImageAnimation['crop']>();

  @ViewChild('canvas') canvasRef: ElementRef;

  private app: Application | null = null;
  private bgInstance: CanvasBackground | null = null;
  private dimOverlay: Graphics | null = null;
  private cropRect: Graphics | null = null;
  private scale = 1;
  private mounted = false;

  // Drag state
  private dragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartCropX = 0;
  private dragStartCropY = 0;

  constructor(
    private backgroundService: BackgroundService,
    private cs: CanvasService,
  ) {}

  ngAfterViewInit() {
    this.mounted = true;
    if (this.backgroundDef) {
      this.rebuild();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.mounted) return;

    if (changes['backgroundDef'] && !changes['backgroundDef'].firstChange) {
      this.rebuild();
    }

    if (changes['animation'] && !changes['animation'].firstChange) {
      this.updateCropRect();
    }
  }

  private rebuild() {
    this.cleanup();
    if (!this.backgroundDef || !this.canvasRef) return;

    const el = this.canvasRef.nativeElement;
    const containerWidth = el.parentElement?.clientWidth || 480;
    const previewWidth = containerWidth;
    const previewHeight = Math.round(previewWidth * (9 / 16));
    this.scale = previewWidth / _screenX;

    const app = new Application({
      width: previewWidth,
      height: previewHeight,
      backgroundAlpha: 1,
      backgroundColor: 0xf9fafb,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
    });

    el.appendChild(app.view);
    this.app = app;

    // Render background only
    this.bgInstance = this.backgroundService._getInstance(this.backgroundDef, {
      isPortrait: this.cs.isPortrait,
    });
    if (this.bgInstance) {
      this.bgInstance.container.scale.set(this.scale);
      app.stage.addChild(this.bgInstance.container);
    }

    // Dim overlay (sits between bg and crop rect)
    this.dimOverlay = new Graphics();
    app.stage.addChild(this.dimOverlay);

    // Crop selection rectangle
    this.cropRect = new Graphics();
    this.cropRect.interactive = true;
    this.cropRect.cursor = 'move';
    app.stage.addChild(this.cropRect);

    this.updateCropRect();
    this.bindDrag();
  }

  private updateCropRect() {
    if (!this.cropRect || !this.dimOverlay) return;
    this.cropRect.clear();
    this.dimOverlay.clear();

    const crop = this.animation?.crop;
    const fullW = _screenX * this.scale;
    const fullH = _screenY * this.scale;

    if (!crop) {
      // No crop set — draw full-frame dashed outline as hint
      this.cropRect.lineStyle(1, 0x9ca3af, 0.5);
      this.cropRect.drawRect(0, 0, fullW, fullH);
      return;
    }

    // crop.x/y are relative offsets, scale is a multiplier
    const s = crop.scale || 1;
    const w = fullW / s;
    const h = fullH / s;
    const cx = -crop.x * fullW;
    const cy = -crop.y * fullH;

    // Dim overlay with hole punched out for the crop area
    this.dimOverlay.beginFill(0x000000, 0.35);
    this.dimOverlay.drawRect(0, 0, fullW, Math.max(0, cy));
    this.dimOverlay.drawRect(0, cy + h, fullW, Math.max(0, fullH - cy - h));
    this.dimOverlay.drawRect(0, cy, Math.max(0, cx), h);
    this.dimOverlay.drawRect(cx + w, cy, Math.max(0, fullW - cx - w), h);
    this.dimOverlay.endFill();

    // Crop border
    this.cropRect.lineStyle(2, 0x3b82f6, 0.9);
    this.cropRect.drawRect(cx, cy, w, h);

    // Transparent hit area for drag
    this.cropRect.beginFill(0x000000, 0.001);
    this.cropRect.drawRect(0, 0, fullW, fullH);
    this.cropRect.endFill();
  }

  private bindDrag() {
    if (!this.cropRect) return;

    this.cropRect.on('pointerdown', (e: any) => {
      this.dragging = true;
      const pos = e.data.global;
      this.dragStartX = pos.x;
      this.dragStartY = pos.y;
      this.dragStartCropX = this.animation?.crop?.x ?? 0;
      this.dragStartCropY = this.animation?.crop?.y ?? 0;
    });

    this.cropRect.on('pointermove', (e: any) => {
      if (!this.dragging) return;
      const pos = e.data.global;
      const dx = (pos.x - this.dragStartX) / (this.scale * _screenX);
      const dy = (pos.y - this.dragStartY) / (this.scale * _screenY);

      const newCrop = {
        x: +(this.dragStartCropX - dx).toFixed(3),
        y: +(this.dragStartCropY - dy).toFixed(3),
        scale: this.animation?.crop?.scale ?? 1,
      };

      this.cropChange.emit(newCrop);
    });

    this.cropRect.on('pointerup', () => {
      this.dragging = false;
    });

    this.cropRect.on('pointerupoutside', () => {
      this.dragging = false;
    });
  }

  private cleanup() {
    this.dragging = false;
    if (this.app) {
      this.app.destroy(true, {
        children: true,
        texture: false,
        baseTexture: false,
      });
      this.app = null;
    }
    this.bgInstance = null;
    this.dimOverlay = null;
    this.cropRect = null;
  }

  ngOnDestroy() {
    this.cleanup();
  }
}
